import { useEffect, useState } from "react";
import type { MutableEncryptedBlob, RepositoryView } from "@baton/contracts";
import type { DashboardApi } from "./api.js";
import type { CryptoWorkerClient } from "./crypto-client.js";
import { Modal } from "./components/modal.js";
import { RepositoryAccess } from "./repository-access.js";

/** New project: encrypted name plus the repositories the viewer can already reach. */
export function ProjectCreateDialog(props: {
  open: boolean;
  onClose(): void;
  api: DashboardApi;
  worker: CryptoWorkerClient;
  repositories: RepositoryView[];
  onCreated(projectId: string): Promise<void> | void;
  onRefreshRepositories(): Promise<void>;
}) {
  const [name, setName] = useState("");
  const [repositoryIds, setRepositoryIds] = useState<string[]>([]);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (props.open) { setName(""); setRepositoryIds([]); setError(null); setWorking(false); }
  }, [props.open]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setWorking(true); setError(null);
    try {
      const id = crypto.randomUUID();
      const nameCiphertext = await props.worker.call<MutableEncryptedBlob>("encryptProjectName", { id, revision: 1, name });
      await props.api.createProject({ id, nameCiphertext, nameRevision: 1, repositoryIds, idempotencyKey: crypto.randomUUID() });
      await props.onCreated(id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not create the project");
      setWorking(false);
    }
  }

  return <Modal open={props.open} onClose={props.onClose} title="New project">
    <form className="project-create" onSubmit={submit} data-testid="project-create-dialog">
      <label>Project name
        <input value={name} onChange={(event) => setName(event.target.value)} placeholder="My project" maxLength={120} required data-autofocus />
      </label>
      <p className="muted compact">Choose the repositories your team will work on. Your general agent is added automatically.</p>
      {props.repositories.length > 0 && <fieldset>
        <legend>Import repositories</legend>
        {props.repositories.map((repository) => <label key={repository.id} className="check-row">
          <input
            type="checkbox"
            checked={repositoryIds.includes(repository.id)}
            onChange={(event) => setRepositoryIds((current) => event.target.checked
              ? [...current, repository.id]
              : current.filter((id) => id !== repository.id))}
          />
          {repository.ownerHandle}/{repository.name}
        </label>)}
      </fieldset>}
      {props.open && <RepositoryAccess api={props.api} onRefresh={props.onRefreshRepositories} />}
      {error && <p className="error">{error}</p>}
      <div className="modal-actions">
        <button type="button" className="secondary" onClick={props.onClose} disabled={working}>Cancel</button>
        <button type="submit" className="primary" disabled={working || !name.trim()}>{working ? "Creating…" : "Create project"}</button>
      </div>
    </form>
  </Modal>;
}
